import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
// @mui
import { Stack, Box, Typography, Button, Chip } from '@mui/material';
// component
import Iconify from '../../../components/Iconify';
import TreatmentFilterSelect from '../../../components/leadDetail/TreatmentFilterSelect';
import PaymentsTable from '../payment/PaymentsTable';
// utils
import { parseApiDate } from '../../../utils/smartReminderRules';
import { derivePaymentCardStatus, statusFilterBucket, STATUS_FILTER_ORDER, STATUS_FILTER_LABELS } from '../payment/paymentCardStatus';
// redux
import { useSelector, dispatch } from '../../../redux/store';
import { getLeadTreatmentPayment, getLeadAllTreatmentPayments } from '../../../redux/slices/lead';
import { setCurrentTreatmentDataGlobal } from '../../../redux/slices/multiTreatment';

// ------------------------------------------------------------------
export default function PaymentRefunds({ leadDetail, handleMenuItems }) {
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const { paymentComplete } = useSelector((state) => state.lead);

    // Local treatment filter — the Payments tab owns it; no global active-treatment read.
    const [filterTreatmentId, setFilterTreatmentId] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');

    const treatments = useMemo(() => leadDetail?.lead_treatments || [], [leadDetail]);

    // Refetch the list whenever the local filter changes. 'all' aggregates every
    // treatment's payments (tagged with lead_treatment); a specific id scopes to one.
    useEffect(() => {
        if (!leadDetail?.id) return;
        if (filterTreatmentId === 'all') {
            dispatch(getLeadAllTreatmentPayments(practiceId, leadDetail.id, treatments));
        } else {
            dispatch(getLeadTreatmentPayment(practiceId, leadDetail.id, filterTreatmentId));
        }
        setStatusFilter('all');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterTreatmentId, leadDetail?.id]);

    const refreshList = useCallback(() => {
        if (!leadDetail?.id) return;
        if (filterTreatmentId === 'all') {
            dispatch(getLeadAllTreatmentPayments(practiceId, leadDetail.id, treatments));
        } else {
            dispatch(getLeadTreatmentPayment(practiceId, leadDetail.id, filterTreatmentId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [practiceId, leadDetail?.id, filterTreatmentId]);

    const payments = useMemo(() => paymentComplete || [], [paymentComplete]);

    // Newest-first by parseApiDate(created_at) — never trust API/array order.
    const sortedPayments = useMemo(
        () =>
            payments.slice().sort((a, b) => {
                const aTime = parseApiDate(a?.created_at)?.valueOf() || 0;
                const bTime = parseApiDate(b?.created_at)?.valueOf() || 0;
                return bTime - aTime;
            }),
        [payments]
    );

    const paymentsWithStatus = useMemo(
        () => sortedPayments.map((p) => ({ payment: p, status: derivePaymentCardStatus(p) })),
        [sortedPayments]
    );

    // 'received' (legacy Take Payment records with mode PHONE/INPRACTICE/
    // BANK_TRANSFER/OTHER — money already received even before `paid_at` is
    // backfilled) counts toward Paid alongside anything with a real paid_at.
    const paid = useMemo(
        () =>
            paymentsWithStatus.reduce(
                (sum, { payment, status }) => ((payment?.paid_at || status === 'received') ? sum + (Number(payment.amount) || 0) : sum),
                0
            ),
        [paymentsWithStatus]
    );

    // 'noLink' (unpaid, no Stripe link sent yet — e.g. a manual deposit taken
    // by phone) is still money the practice expects, so it counts toward
    // Awaiting alongside payments with an open/awaiting Stripe link.
    const awaiting = useMemo(
        () =>
            paymentsWithStatus.reduce(
                (sum, { payment, status }) => ((status === 'awaiting' || status === 'noLink') ? sum + (Number(payment.amount) || 0) : sum),
                0
            ),
        [paymentsWithStatus]
    );

    const statusCounts = useMemo(() => {
        const counts = {};
        paymentsWithStatus.forEach(({ status }) => {
            const bucket = statusFilterBucket(status);
            counts[bucket] = (counts[bucket] || 0) + 1;
        });
        return counts;
    }, [paymentsWithStatus]);

    const visiblePayments = useMemo(
        () =>
            statusFilter === 'all'
                ? paymentsWithStatus
                : paymentsWithStatus.filter(({ status }) => statusFilterBucket(status) === statusFilter),
        [paymentsWithStatus, statusFilter]
    );

    const treatmentNameFor = (payment) => {
        if (payment?.lead_treatment_name) return payment.lead_treatment_name;
        const t = treatments.find((tr) => String(tr.id) === String(payment?.lead_treatment));
        if (t) return t.treatment;
        if (filterTreatmentId !== 'all') {
            const selected = treatments.find((tr) => String(tr.id) === String(filterTreatmentId));
            return selected?.treatment || '';
        }
        return '';
    };

    const treatmentIdFor = (payment) => payment?.lead_treatment || (filterTreatmentId === 'all' ? treatments?.[0]?.id : filterTreatmentId);

    const handleTakePayment = useCallback(() => {
        const activeTreatment =
            filterTreatmentId === 'all'
                ? treatments?.[0]
                : treatments.find((t) => String(t.id) === String(filterTreatmentId)) || treatments?.[0];
        dispatch(
            setCurrentTreatmentDataGlobal({
                lead_id: leadDetail?.id || '',
                first_name: leadDetail?.first_name || '',
                last_name: leadDetail?.last_name || '',
                treatmentData: activeTreatment,
            })
        );
        handleMenuItems('Take a Payment');
    }, [filterTreatmentId, treatments, leadDetail, handleMenuItems]);

    const showTreatment = treatments.length > 1;

    return (
        <Stack spacing={2}>
            <Stack
                direction="row"
                flexWrap="wrap"
                alignItems="center"
                justifyContent="space-between"
                columnGap={1.5}
                rowGap={1}
            >
                <Stack direction="row" flexWrap="wrap" alignItems="center" columnGap={1} rowGap={1}>
                    {showTreatment && (
                        <TreatmentFilterSelect treatments={treatments} value={filterTreatmentId} onChange={setFilterTreatmentId} />
                    )}
                    {payments.length > 0 &&
                        STATUS_FILTER_ORDER.filter((s) => s === 'all' || s === 'awaiting' || s === 'paid' || s === 'expired' || (statusCounts[s] || 0) > 0).map((s) => (
                            <Chip
                                key={s}
                                label={s === 'all' ? `${STATUS_FILTER_LABELS[s]} (${paymentsWithStatus.length})` : `${STATUS_FILTER_LABELS[s]} (${statusCounts[s] || 0})`}
                                color={statusFilter === s ? 'primary' : 'default'}
                                variant={statusFilter === s ? 'filled' : 'outlined'}
                                onClick={() => setStatusFilter(s)}
                                data-testid={`payment-filter-chip-${s}`}
                            />
                        ))}
                </Stack>

                <Stack direction="row" flexWrap="wrap" alignItems="center" columnGap={1.5} rowGap={1}>
                    {payments.length > 0 && (
                        <Typography variant="body2" color="text.secondary" data-testid="payment-summary-strip">
                            Paid £{paid.toFixed(2).replace(/\.00$/, '')} · Awaiting £{awaiting.toFixed(2).replace(/\.00$/, '')}
                        </Typography>
                    )}
                    <Button
                        variant="contained"
                        startIcon={<Iconify icon="eva:plus-fill" width={18} />}
                        onClick={handleTakePayment}
                        data-testid="take-payment-btn"
                    >
                        Take payment
                    </Button>
                </Stack>
            </Stack>

            <Box>
                {visiblePayments.length > 0 && (
                    <PaymentsTable
                        payments={visiblePayments.map(({ payment }) => payment)}
                        leadDetail={leadDetail}
                        practiceId={practiceId}
                        leadId={leadDetail?.id}
                        treatmentIdFor={treatmentIdFor}
                        treatmentNameFor={treatmentNameFor}
                        showTreatment={showTreatment}
                        onChanged={refreshList}
                    />
                )}
                {payments.length === 0 && (
                    <Stack alignItems="center" spacing={1.5} sx={{ py: 6 }}>
                        <Typography color="text.secondary">No payments yet</Typography>
                        <Button variant="contained" onClick={handleTakePayment} data-testid="empty-take-payment-btn">
                            Take payment
                        </Button>
                    </Stack>
                )}
            </Box>
        </Stack>
    );
}

PaymentRefunds.propTypes = {
    leadDetail: PropTypes.any,
    handleMenuItems: PropTypes.func,
};

PaymentRefunds.defaultProps = {
    handleMenuItems: () => {},
};
